import {
  IDocumentCardStyles,
  PersonaSize,
  DocumentCard,
  Stack,
  StackItem,
  Shimmer,
  ShimmerElementsGroup,
  ShimmerElementType,
  HighContrastSelector,
  getTheme
} from '@fluentui/react';
import React from 'react';
import { useEffect, useState } from 'react';
import { DashCardTop } from './DashCardTop';
import { DashCardBottom } from './DashCardBottom';
import { getQueueStats, QueueStats, QueueConfiguration, trackEvent } from 'services';
import { FacepileList } from './FacepileList';
import { useHistory } from 'react-router-dom';
import { SalusPrimaryButton } from '../../shared/SalusPrimaryButton';
import { AdminContext } from '../../../Store/Admin';

const buttonStyle = { height: 24 };

const cardStyles: IDocumentCardStyles = {
  root: {
    borderRadius: 4,
    borderWidth: 0,
    minWidth: 420,
    maxWidth: '100%',
    height: 187,
    selectors: {
      [HighContrastSelector]: {
        border: '1px solid',
        borderColor: getTheme().palette.white
      }
    }
  }
};

function getCardShimmerGroup(): JSX.Element {
  return (
    <>
      <ShimmerElementsGroup
        flexWrap
        width="100%"
        shimmerElements={[
          { type: ShimmerElementType.line, width: '100%', height: 25 },
          { type: ShimmerElementType.gap, width: '100%', height: 10 }
        ]}
      />
      <ShimmerElementsGroup
        width="100%"
        shimmerElements={[{ type: ShimmerElementType.line, width: '100%', height: 85 }]}
      />
    </>
  );
}

// TODO: Look into other options
// https://microsoft.visualstudio.com/Xbox/_workitems/edit/36760705
// In general the email a user logs in with will match the email we have for them in our system,
// except in the case of guest accounts, in which case the email in our system will have a slightly different form,
// basically user@gmail.com will become user_gmail.com#ext#@microsoft.onmicrosoft.com
// this function cleans that gross frankenstein email back to its regular self
const cleanEmail = (email: string): string => {
  const badExtension = '#ext#@microsoft.onmicrosoft.com';

  if (email.includes(badExtension)) {
    const tempUserArr = email.split('_');
    if (tempUserArr.length !== 2) {
      return email;
    } else {
      email = tempUserArr.join('@');
    }
    email = email.replace(badExtension, '');
  }

  return email;
};

export const userIdsMatch = (userId1: string, userId2: string): boolean => {
  userId1 = userId1.toLocaleLowerCase();
  userId2 = userId2.toLocaleLowerCase();
  return userId2 === userId1 || cleanEmail(userId1) === cleanEmail(userId2);
};

export interface DashCardProps {
  userId: string;
  config: QueueConfiguration;
}

export const DashCard: React.FunctionComponent<DashCardProps> = props => {
  const { state } = React.useContext(AdminContext);
  const isAdmin =
    (props.config.allowedAdmins &&
      props.config.allowedAdmins.length !== 0 &&
      props.config.allowedAdmins.some(item => userIdsMatch(item.userId, props.userId))) ||
    !!state.tenantFull?.members?.find(elem => userIdsMatch(elem.userPrincipalName, props.userId));
  const canModerate = props.config.allowedModerators.some(item => userIdsMatch(item.userId, props.userId)) || isAdmin;
  const [isInitialDataLoading, setInitialDataLoading] = useState<boolean>(true);
  const [stats, setStats] = useState<QueueStats>({
    casesTotal: 0,
    casesAssignedTotal: 0,
    casesTotalExceedingCaseOpenLifetime: 0,
    casesTotalNearingCaseOpenLifetime: 0,
    moderatorsAssigned: []
  });
  const [statsError, setStatsError] = useState(false);
  const [intervalKey, setIntervalKey] = useState<NodeJS.Timeout>();
  const history = useHistory();
  const reviewNavigation = (): void => {
    trackEvent('ReviewButton', { component: 'DashCard', eventType: 'click', queueId: props.config.id });
    history.push({
      pathname: '/review',
      search: `?id=${props.config.id}`,
      state: { passedQueueData: props.config }
    });
  };

  useEffect(() => {
    const updateStats = (): void => {
      getQueueStats(props.config)
        .then(queueStats => {
          setStats(queueStats);
          setInitialDataLoading(false);
        })
        .catch(() => {
          setStatsError(true);
          setInitialDataLoading(false);
        });
    };
    updateStats();
    const interval = setInterval(updateStats, 15000);
    setIntervalKey(interval);
    return (): void => clearInterval(interval);
  }, [props.config]);

  // If the stats call fails, stop hitting the server endlessly.
  useEffect(() => {
    if (statsError && intervalKey) {
      clearInterval(intervalKey);
    }
  }, [statsError]);

  return (
    <DocumentCard styles={cardStyles} className="ms-depth-4">
      <Shimmer
        isDataLoaded={!isInitialDataLoading}
        ariaLabel="Loading content"
        customElementsGroup={getCardShimmerGroup()}
        className="p-3"
      >
        <DashCardTop stats={stats} config={props.config} isAdmin={isAdmin} />
        <DashCardBottom stats={stats} config={props.config} />
        <Stack horizontal>
          <StackItem grow align="center">
            <FacepileList userList={stats.moderatorsAssigned} personaSize={PersonaSize.size24} />
          </StackItem>
          {/* Never show the review button if the queue is notAssignable */}
          {canModerate && !props.config.notAssignable && (
            <StackItem align="center">
              <SalusPrimaryButton text="Review" onClick={reviewNavigation} style={buttonStyle} />
            </StackItem>
          )}
        </Stack>
      </Shimmer>
    </DocumentCard>
  );
};
